import { Invitation, Organization } from "../organization/organizationModel";
import organization from "../../internationalization/organization/en.json";
import candidate from "../../internationalization/candidate/en.json";
import { Namespace, TFunction } from "i18next";
import i18n from "../../internationalization/i18n";
import { Capsule } from "../capsule/capsuleModel";

export interface User {
  id: number;
  email: string;
  password?: string | null;
  firstname: string;
  lastname: string;
  admin?: boolean | null;
  validEmail?: boolean;
  adminOrga: boolean;
  phoneNumber?: string | null;
  usage?: string | null;
  positionInOrganization?: string | null;
  organizationMembershipStatus?: "active" | "disabled";
  capsules?: Capsule[];
  organizationId?: Organization["id"] | null;
  organization?: Organization | null;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  country?: string;
  invitations?: Invitation[];
  stripeCustomerId?: string | null;
}

export interface NewUserData {
  email: string;
  firstname: string;
  lastname: string;
  password: string;
  phoneNumber: string | null;
  usage?: string | null;
  positionInOrganization?: string | null;
  searchedPost: string | null;
  sponsorshipToken?: string;
}

const translateEntries = (
  entries: Record<string, string>,
  keyPrefix: string,
  t: TFunction<Namespace>,
) =>
  Object.entries(entries).map(
    ([k]) => [k, t(`${keyPrefix}.${k}` as never)] as [string, string],
  );

const SUPPORT_USAGES = ["EDUCATION", "PROFESSIONAL_INTEGRATION"];

export const getGuideLink = (usage: string | null | undefined): string => {
  if (usage && SUPPORT_USAGES.includes(usage))
    return "https://www.2minutesmax.com/guide-accompagnement/";
  return "https://www.2minutesmax.com/entreprises/";
};

export const getSupportOrganizationUsages = (t: TFunction<Namespace>) =>
  translateEntries(
    organization.usages.support,
    "organization:usages.support",
    t,
  );

export const getCompanyOrganizationUsages = (t: TFunction<Namespace>) =>
  translateEntries(
    organization.usages.company,
    "organization:usages.company",
    t,
  );

export const getOrganizationUsages = (t: TFunction<Namespace>) => [
  ...getSupportOrganizationUsages(t),
  ...getCompanyOrganizationUsages(t),
];

const getCommonPositions = (t: TFunction<Namespace>) =>
  translateEntries(
    organization.positions.common,
    "organization:positions.common",
    t,
  );

export const getSupportPositions = (t: TFunction<Namespace>) => [
  ...translateEntries(
    organization.positions.support,
    "organization:positions.support",
    t,
  ),
  ...getCommonPositions(t),
];

export const getCompanyPositions = (t: TFunction<Namespace>) => [
  ...translateEntries(
    organization.positions.company,
    "organization:positions.company",
    t,
  ),
  ...getCommonPositions(t),
];

export const getPositions = (t: TFunction<Namespace>) => [
  ...translateEntries(
    organization.positions.support,
    "organization:positions.support",
    t,
  ),
  ...translateEntries(
    organization.positions.company,
    "organization:positions.company",
    t,
  ),
  ...getCommonPositions(t),
];

export const getPosts = (t: TFunction<Namespace>) =>
  translateEntries(candidate.posts, "candidate:posts", t);

const translate = i18n.t.bind(i18n) as TFunction<Namespace>;

// Backward-compatible exports for views that still import static lists.
export const SUPPORT_POSITIONS = getSupportPositions(translate);
export const COMPANY_POSITIONS = getCompanyPositions(translate);
export const POSITIONS = getPositions(translate);
export const POSTS = getPosts(translate);
